import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api, { BACKEND_URL } from '../../lib/api';

const Ledger = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem('token'); const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    api.get('/accounts/chart').then(r => setAccounts(r.data)).catch(console.error);
  }, [navigate]);

  const fetchLedger = async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const r = await api.get(`/accounts/ledger/${selectedAccount}?${params}`);
      setLedger(r.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const exportPDF = () => {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    const token = localStorage.getItem('token');
    window.open(`${BACKEND_URL}/api/accounts/export/ledger/${selectedAccount}?${params}&token=${token}`, '_blank');
  };

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="ledger-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-6 sm:p-8">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1a1a2e] mb-6" data-testid="ledger-title">Ledger</h1>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium mb-1">Account</label>
                <select className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={selectedAccount} onChange={e => setSelectedAccount(e.target.value)} data-testid="ledger-account-select">
                  <option value="">Select Account</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1">Start Date</label><input type="date" className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="ledger-start-date" /></div>
              <div><label className="block text-sm font-medium mb-1">End Date</label><input type="date" className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="ledger-end-date" /></div>
              <button onClick={fetchLedger} className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white hover:opacity-90 rounded-sm px-4 py-2 font-medium" data-testid="fetch-ledger-button">View Ledger</button>
            </div>
          </div>

          {loading ? <div className="text-center py-12">Loading...</div> : ledger ? (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm">
              <div className="px-4 py-3 border-b border-[#e5e7eb] flex justify-between items-center">
                <div>
                  <h2 className="font-semibold text-lg">{ledger.account.code} - {ledger.account.name}</h2>
                  <p className="text-sm text-[#6b7280]">Closing Balance: Rs. {parseFloat(ledger.closing_balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                </div>
                {ledger.transactions.length > 0 && <button onClick={exportPDF} className="flex items-center gap-1 text-[#1E40AF] text-sm font-medium hover:underline" data-testid="export-ledger-pdf"><Download className="w-4 h-4" /> Export PDF</button>}
              </div>
              {ledger.transactions.length === 0 ? (
                <div className="p-8 text-center text-[#6b7280]">No transactions found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ledger-table">
                    <thead className="bg-[#f9fafb] text-[#6b7280]">
                      <tr><th className="py-2 px-4 text-left">Date</th><th className="py-2 px-4 text-left">Entry #</th><th className="py-2 px-4 text-left">Description</th><th className="py-2 px-4 text-right">Debit</th><th className="py-2 px-4 text-right">Credit</th><th className="py-2 px-4 text-right">Balance</th></tr>
                    </thead>
                    <tbody>
                      {ledger.transactions.map((t, i) => (
                        <tr key={i} className="border-t border-[#e5e7eb] hover:bg-[#f9fafb]">
                          <td className="py-2 px-4">{t.date ? new Date(t.date).toLocaleDateString() : '-'}</td>
                          <td className="py-2 px-4 font-mono text-xs">{t.entry_number}</td>
                          <td className="py-2 px-4">{t.description}</td>
                          <td className="py-2 px-4 text-right">{t.debit > 0 ? `Rs. ${t.debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}</td>
                          <td className="py-2 px-4 text-right">{t.credit > 0 ? `Rs. ${t.credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}</td>
                          <td className="py-2 px-4 text-right font-medium">Rs. {t.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-[#f9fafb] font-semibold">
                      <tr><td colSpan="5" className="py-2 px-4">Closing Balance</td><td className="py-2 px-4 text-right">Rs. {parseFloat(ledger.closing_balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          ) : <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-12 text-center text-[#6b7280]">Select an account and click "View Ledger"</div>}
        </main>
      </div>
    </div>
  );
};

export default Ledger;
